import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Camera, Upload, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export function resolveAvatar(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND}${url}`;
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '🙂';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// Crop the selected image to a square blob at the chosen zoom/position.
async function getCroppedBlob(src, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const size = Math.min(area.width, area.height);
  const canvas = document.createElement('canvas');
  const out = 512;
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, area.x, area.y, size, size, 0, 0, out, out);
  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', 0.9));
}

export default function AvatarPicker() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // crop state
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const areaRef = useRef(null);

  const current = resolveAvatar(user?.avatar_url);

  useEffect(() => {
    if (open && !presets.length) {
      http.get('/avatars/presets').then(r => setPresets(r.data.avatars || [])).catch(() => {});
    }
  }, [open, presets.length]);

  const closeAll = () => { setOpen(false); setCropSrc(null); setZoom(1); setCrop({ x: 0, y: 0 }); };

  const choosePreset = async (url) => {
    setBusy(true);
    try {
      await http.post('/me/avatar', { avatar_url: url });
      await refresh();
      toast.success('Profile photo updated');
      closeAll();
    } catch { toast.error('Could not set that photo.'); }
    finally { setBusy(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB).'); return; }
    setCropSrc(URL.createObjectURL(file));
    setZoom(1); setCrop({ x: 0, y: 0 });
    if (fileRef.current) fileRef.current.value = '';
  };

  const confirmCrop = async () => {
    if (!areaRef.current || !cropSrc) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(cropSrc, areaRef.current);
      const fd = new FormData();
      fd.append('file', blob, 'avatar.jpg');
      await http.post('/me/avatar/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      await refresh();
      toast.success('Profile photo updated');
      closeAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed. Try again.'); }
    finally { setBusy(false); }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await http.delete('/me/avatar'); await refresh(); toast.success('Photo removed'); closeAll(); }
    catch { toast.error('Could not remove photo.'); }
    finally { setBusy(false); }
  };

  const onCropComplete = useCallback((_, areaPixels) => { areaRef.current = areaPixels; }, []);

  return (
    <>
      <button data-testid="avatar-edit-button" onClick={() => setOpen(true)} className="relative group shrink-0" aria-label="Change profile photo">
        <span className="block w-20 h-20 lg:w-24 lg:h-24 rounded-full overflow-hidden ring-2 ring-gold/60 bg-white/10 flex items-center justify-center">
          {current
            ? <img src={current} alt="" className="w-full h-full object-cover" data-testid="avatar-current-image"/>
            : <span className="font-heading text-2xl text-gold">{initials(user?.name)}</span>}
        </span>
        <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gold text-navy flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Camera size={15} strokeWidth={2}/>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-navy/60 backdrop-blur-sm p-0 sm:p-6" data-testid="avatar-modal" onClick={() => !busy && closeAll()}>
          <div className="bg-[#F9F8F6] w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl text-navy">{cropSrc ? 'Adjust your photo' : 'Profile photo'}</p>
              <button onClick={closeAll} disabled={busy} className="text-navy/40 hover:text-navy" data-testid="avatar-modal-close"><X size={20}/></button>
            </div>

            {cropSrc ? (
              <div data-testid="avatar-cropper">
                <div className="relative w-full h-72 bg-navy rounded-2xl overflow-hidden">
                  <Cropper
                    image={cropSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
                    onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
                  />
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <span className="text-xs text-navy/50">Zoom</span>
                  <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={e => setZoom(Number(e.target.value))}
                    data-testid="avatar-zoom" className="flex-1 accent-gold"/>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => { setCropSrc(null); }} disabled={busy} className="px-4 py-3 rounded-full border border-navy/15 text-navy/70 text-sm">Back</button>
                  <button onClick={confirmCrop} disabled={busy} data-testid="avatar-crop-save"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full py-3 text-sm font-medium disabled:opacity-50">
                    <Check size={16}/> {busy ? 'Saving…' : 'Save photo'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="avatar-file-input"/>
                <div className="flex gap-2 mb-5">
                  <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="avatar-upload-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full py-3 text-sm font-medium disabled:opacity-50">
                    <Upload size={15}/> Upload a photo
                  </button>
                  {user?.avatar_url && (
                    <button onClick={removePhoto} disabled={busy} data-testid="avatar-remove-button"
                      className="px-4 rounded-full border border-navy/15 text-navy/70 text-sm">Remove</button>
                  )}
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-navy/50 font-semibold mb-3">Or pick one</p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                  {presets.map((url) => {
                    const selected = user?.avatar_url === url;
                    return (
                      <button key={url} onClick={() => choosePreset(url)} disabled={busy} data-testid="avatar-preset"
                        className={`relative aspect-square rounded-full overflow-hidden ring-2 transition-smooth ${selected ? 'ring-gold' : 'ring-transparent hover:ring-navy/20'}`}>
                        <img src={url} alt="" className="w-full h-full object-cover"/>
                        {selected && <span className="absolute inset-0 bg-navy/40 flex items-center justify-center"><Check size={18} className="text-gold"/></span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
